import { Stack } from '@mui/material';
import { Form, FormRef } from 'common/form';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { useRef, useState, useEffect } from 'react';
import {
  sourcingSharedChooseChannelSchema,
  SourcingSharedChooseChannelFormData,
  getSourcingSharedChooseChannelFields
} from 'modules/sourcing-shared/forms';
import { SHARED_SOURCING_AVAILABLE_CHANNELS } from 'modules/sourcing-shared/constants';
import { Header, HeaderBreadcrumb } from 'common/ui/Header';
import { Channels } from 'generated-graphql/types';
import { useModalV2 } from 'common/modal/hooks/useModalV2';
import { SMSAlertCard } from '../../../common/ui';

type SourcingSharedChooseChannelStepProps = {
  headerBreadcrumbs: HeaderBreadcrumb[];
  defaultFormData: SourcingSharedChooseChannelFormData | null;
  onChooseChannelNext: (props: {
    formData: SourcingSharedChooseChannelFormData;
    shouldClearCustomInvitationMessages: boolean;
  }) => void;
  onHeaderBack: () => void;
};

const CURRENT_BREADCRUMB_TITLE = 'Choose channel';

export const SourcingSharedChooseChannelStep: React.FC<
  SourcingSharedChooseChannelStepProps
> = ({
  headerBreadcrumbs,
  defaultFormData,
  onChooseChannelNext,
  onHeaderBack
}) => {
  const organization = useOrganization();
  const { openModal } = useModalV2();

  const channelsEnabled = organization?.channelsEnabled;

  const formRef = useRef<FormRef<SourcingSharedChooseChannelFormData>>(null);
  const [selectedChannel, setSelectedChannel] = useState<Channels | null>(
    defaultFormData?.channel ?? null
  );

  useEffect(() => {
    if (!formRef?.current) {
      return;
    }

    const subscription = formRef.current.watch(
      (value: SourcingSharedChooseChannelFormData) => {
        setSelectedChannel(value.channel);
      }
    );

    return () => subscription.unsubscribe();
  }, [formRef?.current?.watch]);

  const safeChannels = channelsEnabled ?? [];
  const availableChannels = SHARED_SOURCING_AVAILABLE_CHANNELS.filter(
    (channel) => safeChannels.includes(channel)
  );

  const handleFormSubmit = (data: SourcingSharedChooseChannelFormData) => {
    if (!defaultFormData) {
      onChooseChannelNext({
        formData: data,
        shouldClearCustomInvitationMessages: true
      });
      return;
    }

    if (defaultFormData.channel !== data.channel) {
      openModal({
        title: 'Change channel?',
        subtitle:
          'All invitations and settings will be reset. This cannot be undone.',
        primaryButtonText: 'Cancel',
        secondaryButtonText: 'Confirm',
        secondaryButtonProps: {
          state: 'destructive'
        },
        onSecondaryAction: () =>
          onChooseChannelNext({
            formData: data,
            shouldClearCustomInvitationMessages: true
          })
      });
      return;
    }

    onChooseChannelNext({
      formData: data,
      shouldClearCustomInvitationMessages: false
    });
  };

  return (
    <>
      <Header
        title={CURRENT_BREADCRUMB_TITLE}
        breadcrumbs={headerBreadcrumbs}
        currentBreadcrumb={CURRENT_BREADCRUMB_TITLE}
        onBack={onHeaderBack}
      />
      <Stack direction="column" spacing="40px">
        <Stack direction="column" spacing="40px" maxWidth="700px">
          <Form
            ref={formRef}
            schema={sourcingSharedChooseChannelSchema}
            fields={getSourcingSharedChooseChannelFields({
              availableChannels,
              defaultFormData: defaultFormData ?? { channel: Channels.Email }
            })}
            submitButtonProps={{
              label: 'Next step'
            }}
            onSubmit={handleFormSubmit}
          />
          {selectedChannel === Channels.Sms && (
            <SMSAlertCard twilioPhoneNumber={organization?.twilioPhoneNumber} />
          )}
        </Stack>
      </Stack>
    </>
  );
};
